import type { FieldErrors, Resolver } from 'react-hook-form'

export const MONGODB_CLUSTER_TYPE = 'REPLICASET' as const

export type MongoDBClusterFormValues = {
	name: string
	instance_size: string
	node_count: string
	disk_size_gb: string
	auto_scaling_compute_enabled: boolean
	auto_scaling_compute_min_instance_size: string
	auto_scaling_compute_max_instance_size: string
}

export type MongoDBClusterConfig = {
	name: string
	cluster_type: typeof MONGODB_CLUSTER_TYPE
	instance_size: string
	node_count: number
	disk_size_gb: number
	auto_scaling_compute_enabled: boolean
	auto_scaling_compute_min_instance_size?: string
	auto_scaling_compute_max_instance_size?: string
}

export type MongoDBClusterErrors = Partial<
	Record<keyof MongoDBClusterFormValues, string>
>

export const getMongoDBClusterFormValues = (
	cluster?: MongoDBClusterConfig
): MongoDBClusterFormValues => ({
	name: cluster?.name ?? 'clus01',
	instance_size: cluster?.instance_size ?? '',
	node_count: cluster ? String(cluster.node_count) : '',
	disk_size_gb: cluster ? String(cluster.disk_size_gb) : '',
	auto_scaling_compute_enabled: cluster?.auto_scaling_compute_enabled ?? false,
	auto_scaling_compute_min_instance_size:
		cluster?.auto_scaling_compute_min_instance_size ?? '',
	auto_scaling_compute_max_instance_size:
		cluster?.auto_scaling_compute_max_instance_size ?? ''
})

const getTierNumber = (tier: string): number | null => {
	const match = /^M(\d+)$/i.exec(tier.trim())
	return match ? Number(match[1]) : null
}

const getPositiveInteger = (value: string): number | null => {
	const trimmed = value.trim()
	if (!/^\d+$/.test(trimmed)) return null
	const number = Number(trimmed)
	return Number.isSafeInteger(number) && number > 0 ? number : null
}

export const prepareMongoDBCluster = (
	values: MongoDBClusterFormValues
):
	| { status: 'ready'; cluster: MongoDBClusterConfig }
	| { status: 'invalid'; errors: MongoDBClusterErrors } => {
	const errors: MongoDBClusterErrors = {}
	const name = values.name.trim()
	const instanceSize = values.instance_size.trim().toUpperCase()
	const startingNumber = getTierNumber(instanceSize)
	const nodeCount = getPositiveInteger(values.node_count)
	const diskSize = getPositiveInteger(values.disk_size_gb)

	if (!name) errors.name = 'Enter a cluster name.'
	if (!instanceSize) {
		errors.instance_size = 'Enter an instance size.'
	} else if (startingNumber === null) {
		errors.instance_size = 'Use M followed by digits, such as M10.'
	}
	if (nodeCount === null) {
		errors.node_count = 'Enter a positive odd whole number.'
	} else if (nodeCount % 2 === 0) {
		errors.node_count = 'Node count must be an odd number.'
	}
	if (diskSize === null) {
		errors.disk_size_gb = 'Enter a positive whole number.'
	}

	const minTier = values.auto_scaling_compute_min_instance_size
		.trim()
		.toUpperCase()
	const maxTier = values.auto_scaling_compute_max_instance_size
		.trim()
		.toUpperCase()

	if (values.auto_scaling_compute_enabled) {
		const minNumber = getTierNumber(minTier)
		const maxNumber = getTierNumber(maxTier)

		if (!minTier) {
			errors.auto_scaling_compute_min_instance_size = 'Enter a minimum tier.'
		} else if (minNumber === null) {
			errors.auto_scaling_compute_min_instance_size =
				'Use an M tier such as M10.'
		}
		if (!maxTier) {
			errors.auto_scaling_compute_max_instance_size = 'Enter a maximum tier.'
		} else if (maxNumber === null) {
			errors.auto_scaling_compute_max_instance_size =
				'Use an M tier such as M30.'
		}
		if (minNumber !== null && maxNumber !== null && minNumber > maxNumber) {
			errors.auto_scaling_compute_max_instance_size =
				'Maximum tier must be at least the minimum tier.'
		}
		if (
			startingNumber !== null &&
			minNumber !== null &&
			startingNumber < minNumber
		) {
			errors.instance_size = 'Starting tier must be at least the minimum tier.'
		}
		if (
			startingNumber !== null &&
			maxNumber !== null &&
			startingNumber > maxNumber
		) {
			errors.instance_size = 'Starting tier must not exceed the maximum tier.'
		}
	}

	if (
		Object.keys(errors).length > 0 ||
		nodeCount === null ||
		diskSize === null
	) {
		return { status: 'invalid', errors }
	}

	const cluster: MongoDBClusterConfig = {
		name,
		cluster_type: MONGODB_CLUSTER_TYPE,
		instance_size: instanceSize,
		node_count: nodeCount,
		disk_size_gb: diskSize,
		auto_scaling_compute_enabled: values.auto_scaling_compute_enabled
	}

	if (values.auto_scaling_compute_enabled) {
		cluster.auto_scaling_compute_min_instance_size = minTier
		cluster.auto_scaling_compute_max_instance_size = maxTier
	}
	return { status: 'ready', cluster }
}

export const mongodbClusterResolver: Resolver<MongoDBClusterFormValues> = (
	values
) => {
	const result = prepareMongoDBCluster(values)
	if (result.status === 'ready') return { values, errors: {} }

	const errors: FieldErrors<MongoDBClusterFormValues> = {}
	for (const [name, message] of Object.entries(result.errors)) {
		errors[name as keyof MongoDBClusterFormValues] = {
			type: 'validate',
			message
		}
	}
	return { values: {}, errors }
}
